import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import { useEffect, useState } from "react"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import type { PlacementAssets, ScrapedImage } from "../../types"
import { rescaleDoc } from "./BrandOverlayPanel"
import CardOverlayPreview from "./CardOverlayPreview"
import { useCustomBrandFonts } from "./useCustomBrandFonts"
import { dimsForRatioLabel } from "./overlay-baking"
import type { PlacementSlotKey } from "./PlacementOverlayEditorModal"

/**
 * Full-page placement preview: one placement blown up to (near) fullscreen,
 * media + assigned layout rendered with the same override/fit rules as the
 * tiles — i.e. exactly what bakes at save. ←/→ (buttons or arrow keys) flip
 * through the placements; Esc or backdrop closes.
 */

const PREVIEW_SLOTS: Array<{
  slot: PlacementSlotKey
  label: string
  ratio: string
}> = [
  // 4:5 covers every feed surface (FB Feed / Marketplace / Search, IG Stream /
  // Explore / Profile Feed) — the old 1.91:1 `landscape` slot is retired.
  { slot: "portrait", label: "Feed", ratio: "4:5" },
  { slot: "stories", label: "Stories", ratio: "9:16" },
  { slot: "square", label: "Square", ratio: "1:1" },
]

export interface PlacementPreviewTarget {
  card: ScrapedImage
  slot: PlacementSlotKey
}

interface PlacementPreviewModalProps {
  target: PlacementPreviewTarget | null
  /** The card's per-slot custom variants (crops/uploads). */
  placements: PlacementAssets | undefined
  onClose: () => void
}

export default function PlacementPreviewModal({
  target,
  placements,
  onClose,
}: PlacementPreviewModalProps) {
  const [activeSlot, setActiveSlot] = useState<PlacementSlotKey>("portrait")
  useCustomBrandFonts()

  useEffect(() => {
    if (target) setActiveSlot(target.slot)
  }, [target])

  const activeIndex = PREVIEW_SLOTS.findIndex(s => s.slot === activeSlot)
  const step = (direction: -1 | 1): void => {
    const next =
      PREVIEW_SLOTS[
        (activeIndex + direction + PREVIEW_SLOTS.length) % PREVIEW_SLOTS.length
      ]
    setActiveSlot(next.slot)
  }

  useEffect(() => {
    if (!target) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose()
      if (e.key === "ArrowLeft") step(-1)
      if (e.key === "ArrowRight") step(1)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  })

  if (!target) return null

  const cfg = PREVIEW_SLOTS[activeIndex] ?? PREVIEW_SLOTS[0]
  const { card } = target
  const variant = placements?.[cfg.slot]
  const media = variant ?? card
  const isCustom = !!variant
  const override = card.overlayDocByPlacement?.[cfg.slot]
  const coverFit = isCustom || !!override
  const overlayDoc = card.overlayDoc
    ? (override ??
      (isCustom
        ? rescaleDoc(card.overlayDoc, dimsForRatioLabel(cfg.ratio))
        : card.overlayDoc))
    : null

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-black/90"
      onClick={onClose}
      role="dialog"
      aria-label={`${cfg.label} placement preview`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-2">
          <span className="text-white font-semibold">{cfg.label}</span>
          <span className="text-system-gray-400 text-sm">{cfg.ratio}</span>
          {override && (
            <span className="rounded bg-brand/20 border border-brand/50 px-1.5 py-0.5 text-[10px] font-semibold text-brand">
              Custom arrangement
            </span>
          )}
          {isCustom && (
            <span className="rounded bg-system-gray-800 px-1.5 py-0.5 text-[10px] font-semibold text-system-gray-300">
              AI variant
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-full text-system-gray-300 hover:text-white hover:bg-white/10"
          aria-label="Close preview"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      </div>

      {/* Stage */}
      <div className="flex-1 flex items-center justify-center gap-4 px-6 pb-2 min-h-0">
        <button
          type="button"
          onClick={e => {
            e.stopPropagation()
            step(-1)
          }}
          className="p-2 rounded-full text-system-gray-300 hover:text-white hover:bg-white/10"
          aria-label="Previous placement"
        >
          <ChevronLeftIcon className="w-7 h-7" />
        </button>

        <div
          className="relative h-full max-h-full max-w-[80vw] overflow-hidden rounded-xl bg-system-gray-900 ring-1 ring-white/10"
          style={{ aspectRatio: cfg.ratio.replace(":", " / ") }}
          onClick={e => e.stopPropagation()}
        >
          {media.type === "video" ? (
            <video
              src={media.url}
              poster={media.thumbnailUrl || undefined}
              muted
              loop
              autoPlay
              playsInline
              controls
              className={cn(
                "absolute inset-0 h-full w-full",
                coverFit ? "object-cover" : "object-contain",
              )}
            />
          ) : (
            <img
              src={media.url || media.thumbnailUrl}
              alt={cfg.label}
              className={cn(
                "absolute inset-0 h-full w-full",
                coverFit ? "object-cover" : "object-contain",
              )}
            />
          )}
          {overlayDoc && (
            <CardOverlayPreview
              doc={overlayDoc}
              fit={coverFit ? "cover" : "contain"}
            />
          )}
        </div>

        <button
          type="button"
          onClick={e => {
            e.stopPropagation()
            step(1)
          }}
          className="p-2 rounded-full text-system-gray-300 hover:text-white hover:bg-white/10"
          aria-label="Next placement"
        >
          <ChevronRightIcon className="w-7 h-7" />
        </button>
      </div>

      {/* Slot switcher */}
      <div className="flex items-center justify-center gap-2 pb-5">
        {PREVIEW_SLOTS.map(slotCfg => (
          <button
            key={slotCfg.slot}
            type="button"
            onClick={e => {
              e.stopPropagation()
              setActiveSlot(slotCfg.slot)
            }}
            className={cn(
              "btn btn-xs border-system-gray-700",
              slotCfg.slot === activeSlot
                ? "bg-brand/20 border-brand/60 text-white"
                : "bg-system-gray-800 text-system-gray-300 hover:text-white",
            )}
          >
            {slotCfg.label}
          </button>
        ))}
      </div>
    </div>
  )
}
